import { useState } from 'react';
import HelpText from 'components/HelpText';
import { Button, FormControlLabel, makeStyles, Switch } from '@material-ui/core';
import NumberInput from 'components/Input/NumberInput';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { SettingsState } from 'redux/ducks/settings';

interface MaxRegistrationAmountProps {
  settings: Pick<
    SettingsState,
    'maxRegistrationAmount' | 'lockedByAdmin' | 'overrideChildSettings'
  >;
  onUpdateSettings: (data) => void;
  hasSubscribedAccounts: boolean;
}

const useStyles = makeStyles(() => {
  return {
    editamount: {
      display: 'flex',
      flexDirection: 'row',
      marginLeft: '36px',
      marginBottom: '12px'
    },
    formLabel: {
      marginRight: '12px',
      fontWeight: 600
    },
    submit: {
      marginLeft: '16px',
      marginRight: '4px'
    }
  };
});

const MaxRegistrationAmount: React.FunctionComponent<MaxRegistrationAmountProps> = ({
  settings,
  onUpdateSettings,
  hasSubscribedAccounts
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const { maxRegistrationAmount, lockedByAdmin, overrideChildSettings } = settings;
  const { maxRegistrationAmount: settingIsLockedByAdmin } = lockedByAdmin || {};
  const formattedMaxRegistrationAmount = maxRegistrationAmount
    ? maxRegistrationAmount / 1000
    : null;

  const [amountInKg, setAmountInKg] = useState(formattedMaxRegistrationAmount || 5.0);

  const handleUpdateSettings = (newAmountInKg?: number) => {
    let updateSettings: Partial<SettingsState> = {
      maxRegistrationAmount: newAmountInKg * 1000
    };

    if (!newAmountInKg && overrideChildSettings?.maxRegistrationAmount?.enabled) {
      updateSettings = {
        ...updateSettings,
        overrideChildSettings: {
          ...overrideChildSettings,
          maxRegistrationAmount: {
            enabled: false,
            overridePrevious: true
          }
        }
      };
    }

    onUpdateSettings(updateSettings);
  };

  const handleOverrideSubAccounts = () => {
    onUpdateSettings({
      overrideChildSettings: {
        ...overrideChildSettings,
        maxRegistrationAmount: {
          enabled: Boolean(maxRegistrationAmount)
        }
      }
    });
  };

  return (
    <div className={'settingToggle'}>
      <div>
        <HelpText helpText={intl.formatMessage({ id: 'help.settings.maxRegistrationAmount' })} />
        <FormControlLabel
          control={
            <Switch
              color={'primary'}
              disabled={settingIsLockedByAdmin}
              checked={Boolean(maxRegistrationAmount)}
              onChange={(_, checked) => {
                handleUpdateSettings(checked ? amountInKg : undefined);
              }}
            />
          }
          label={intl.formatMessage({ id: 'settings.maxRegistrationAmount' })}
        />
      </div>
      {maxRegistrationAmount && (
        <div className={classes.editamount}>
          <FormControlLabel
            labelPlacement='start'
            classes={{
              label: classes.formLabel
            }}
            control={
              <NumberInput
                allowEmptyFormatting
                autoComplete='off'
                allowNegative={false}
                suffix=' kg'
                name='amount'
                value={amountInKg}
                disabled={settingIsLockedByAdmin}
                onValueChange={(values) => setAmountInKg(values.floatValue)}
              />
            }
            label={intl.formatMessage({ id: 'settings.maxRegistrationAmount.amount' })}
          />
          <Button
            className={classes.submit}
            color='primary'
            variant='contained'
            disabled={settingIsLockedByAdmin || !amountInKg}
            onClick={() => handleUpdateSettings(amountInKg)}
          >
            {intl.formatMessage({ id: 'base.save' })}
          </Button>
          {hasSubscribedAccounts && (
            <Button
              disabled={amountInKg !== formattedMaxRegistrationAmount || settingIsLockedByAdmin}
              color='primary'
              variant='contained'
              onClick={handleOverrideSubAccounts}
            >
              {intl.formatMessage({ id: 'settings.targets.applyToSubaccounts' })}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default MaxRegistrationAmount;
